import React, { useEffect } from "react";
import { Gap, MyInput, MyButton, MyAutocomplete } from "../../atoms";

import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Table from "./Table";
import MyDialog from "../../molecules/Dialog";
import FormHelperText from "@mui/material/FormHelperText";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import { useFormik } from "formik";
import AlertMessage from "../../../components/molecules/AlertMessage";
import {
  getUsers,
  postNewUser,
} from "../../../config/Redux/UserManagement/actions";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import { PRIMARY } from "../../../utils/constansColors";
import { makeStyles } from "@mui/styles";
import * as yup from "yup";
import {
  getProvince,
  getKabKot,
  getKecamatan,
  getKelurahan,
  getKomisarisBasis,
} from "../../../config/Redux/Global/actions";

const useStyles = makeStyles({
  root: {
    "& .Mui-selected": {
      backgroundColor: PRIMARY,
      color: "white",
      border: "none",
    },
  },
  tableBody: {
    backgroundColor: "#FFFFFF",
    width: "100%",
    tableLayout: "fixed",
    marginTop: "10px",
    fontSize: "25px",
    borderRadius: 5,
  },
  tableHeader: {
    backgroundColor: "transparant",
    width: "100%",
    tableLayout: "fixed",
    marginTop: "10px",
    color: "#757575",
    fontWeight: 700,
  },
});

const validationSchemaCreate = yup.object().shape({
  name: yup.string("Masukan Alamat").required("Silahkan Masukan Alamat"),
  email: yup.string("Masukan Email").required("Silahkan Masukan Email"),
  phone: yup
    .string("Masukan Nomor Ponsel")
    .required("Silahkan Masukan Nomor Ponsel"),
  levelExco: yup
    .object()
    .shape({
      label: yup.string(),
      id: yup.string(),
    })
    .nullable()
    .required("Silahkan Masukan Tingkat Exco"),
  provinsi: yup
    .object()
    .when("levelExco", {
      is: (levelExco) =>
        (levelExco && levelExco?.id === "2") ||
        (levelExco && levelExco.id === "3") ||
        (levelExco && levelExco.id === "4") ||
        (levelExco && levelExco.id === "5"),
      then: yup
        .object()
        .shape({ label: yup.string(), id: yup.string() })
        .nullable()
        .required("Silahkan Masukan Provinsi"),
    })
    .nullable(),
  kabkot: yup
    .object()
    .when("levelExco", {
      is: (levelExco) =>
        (levelExco && levelExco?.id === "3") ||
        (levelExco && levelExco.id === "4") ||
        (levelExco && levelExco.id === "5"),
      then: yup
        .object()
        .shape({ label: yup.string(), id: yup.string() })
        .nullable()
        .required("Silahkan Masukan Kabupaten / Kota"),
    })
    .nullable(),
  kecamatan: yup
    .object()
    .when("levelExco", {
      is: (levelExco) =>
        (levelExco && levelExco?.id === "4") ||
        (levelExco && levelExco?.id === "5"),
      then: yup
        .object()
        .shape({ label: yup.string(), id: yup.string() })
        .nullable()
        .required("Silahkan Masukan Kecamatan"),
    })
    .nullable(),
  kelurahan: yup
    .object()
    .when("levelExco", {
      is: (levelExco) => levelExco && levelExco?.id === "5",
      then: yup
        .object()
        .shape({ label: yup.string(), id: yup.string() })
        .nullable()
        .required("Silahkan Masukan Kelurahan / Desa"),
    })
    .nullable(),
  komisarisBasis: yup
    .object()
    .when("levelExco", {
      is: (levelExco) => levelExco && levelExco?.id === "7",
      then: yup
        .object()
        .shape({ label: yup.string(), id: yup.string() })
        .nullable()
        .required("Silahkan Masukan Komisaris Basis"),
    })
    .nullable(),
  password: yup.string().required("Silahkan Masukan Kata Sandi Baru"),
  confirmPassword: yup
    .string()
    .required("Silahkan Konfirmasi Kata Sandi")
    .oneOf([yup.ref("password"), null], "Kata Sandi tidak sama"),
});

const UserManagement = () => {
  const dispatch = useDispatch();
  const [openDialogUser, setOpenDialogUser] = React.useState(false);
  const [levelExco, setLevelExco] = React.useState(null);
  const [idProvinsi, setIdProvinsi] = React.useState(null);
  const [showPasswordNew, setShowPasswordNew] = React.useState(false);
  const [showPasswordNewConfirm, setShowPasswordNewConfirm] =
    React.useState(false);

  // alert
  const [openDialogAlert, setOpenDialogAlert] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [status, setStatus] = React.useState(null);

  // selector
  const province = useSelector((state) => state.global.dataProvince);
  const kabkot = useSelector((state) => state.global.dataKabKot);
  const kecamatan = useSelector((state) => state.global.dataKecamatan);
  const kelurahan = useSelector((state) => state.global.dataKelurahan);
  const komisarisBasis = useSelector((state) => state.global.dataKomisarisBasis);

  // options
  // options
  let optionsProv = province?.data?.map((item) => ({
    label: item.nama_prov_kemdagri,
    id: item.kode_prov_bps,
  }));

  let optionsKabKot = kabkot?.data?.map((item) => ({
    label: item.nama_kab_kota_kemdagri,
    id: item.kode_kab_kota_bps,
  }));
  let optionsKecamatan = kecamatan?.data?.map((item) => ({
    label: item.nama_kec_bps,
    id: item.kode_kec_bps,
  }));

  let optionsKelurahan = kelurahan?.data?.map((item) => ({
    label: item.nama_kel_des_bps,
    id: item.kode_kel_desa_bps,
  }));

  let optionsKomisarisBasis = komisarisBasis?.data?.map((item) => ({
    label: item.tingkat_puk_industri,
    id: item.tingkat_puk_industri,
  }));

  // formik add user
  const formikProfile = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      levelExco: null,
      provinsi: null,
      kabkot: null,
      kecamatan: null,
      kelurahan: null,
      komisarisBasis: null,
    },
    enableReinitialize: true,
    validationSchema: validationSchemaCreate,
    onSubmit: async (values) => {
      let payload = {
        name: values.name,
        email: values.email,
        nik: "0",
        phone: values.phone,
        password: values.password,
        id_tingkat_exco: values?.levelExco?.id,
        id_provinsi: values?.provinsi?.id || null,
        id_kabkot: values?.kabkot?.id || null,
        id_kecamatan: values?.kecamatan?.id || null,
        id_kelurahan: values?.kelurahan?.id || null,
        tingkat_puk_industri: values?.komisarisBasis?.id || null,
      };

      let resp = await dispatch(postNewUser(payload));
      if (resp.type === "ADD_NEW_USER_SUCCESS") {
        handleCloseDialogUser();
        setOpenDialogAlert(true);
        setMessage(resp.payload.message);
        setStatus("");
        dispatch(getUsers(1));
      } else {
        setOpenDialogAlert(true);
        setMessage(resp.payload.data.message);
        setStatus("error");
      }
    },
  });

  // new user
  const handleOpenDialogUser = () => {
    setOpenDialogUser(true);
    formikProfile.resetForm();
  };

  const handleCloseDialogUser = () => {
    setOpenDialogUser(false);
    formikProfile.resetForm();
  };

  const handleClickShowPassword = (param) => {
    if (param === "newPass") {
      setShowPasswordNew(!showPasswordNew);
    }
    if (param === "passNewConfirm") {
      setShowPasswordNewConfirm(!showPasswordNewConfirm);
    }
  };

  const options = [
    { label: "EXCO Pusat", id: 1 },
    { label: "EXCO Provinsi", id: 2 },
    { label: "EXCO Kabupaten / Kota", id: 3 },
    { label: "EXCO Kecamatan", id: 4 },
    { label: "EXCO Kelurahan / Desa", id: 5 },
    // { label: "EXCO Komisaris RT / RW", id: 6 },
    { label: "EXCO Komisaris Basis", id: 7 },
  ];
  // add new user
  const renderFormNewData = (
    <>
      <div align="center">
        <h2>
          <b>Tambah Pengguna</b>
        </h2>
      </div>
      <MyInput
        id="name"
        name="name"
        label="Nama Lengkap*"
        fullWidth
        size="medium"
        value={formikProfile.values.name}
        onChange={formikProfile.handleChange}
        error={formikProfile.touched.name && Boolean(formikProfile.errors.name)}
        helperText={formikProfile.touched.name && formikProfile.errors.name}
      />
      <Gap height={20} />
      <MyInput
        id="email"
        name="email"
        label="Email*"
        fullWidth
        size="medium"
        value={formikProfile.values.email}
        onChange={formikProfile.handleChange}
        error={
          formikProfile.touched.email && Boolean(formikProfile.errors.email)
        }
        helperText={formikProfile.touched.email && formikProfile.errors.email}
      />
      <Gap height={20} />
      <MyInput
        id="phone"
        name="phone"
        label="Nomor HP*"
        fullWidth
        size="medium"
        value={formikProfile.values.phone}
        onChange={formikProfile.handleChange}
        error={
          formikProfile.touched.phone && Boolean(formikProfile.errors.phone)
        }
        helperText={formikProfile.touched.phone && formikProfile.errors.phone}
      />
      <Gap height={20} />
      <MyAutocomplete
        options={options}
        fullWidth
        size="medium"
        label="Tingkat Exco*"
        id="levelExco"
        name="levelExco"
        value={formikProfile.values.levelExco}
        onChange={(e, value) => {
          setLevelExco(value.id);
          formikProfile.setFieldValue("levelExco", value);
          if (value.id === 7) {
            dispatch(getKomisarisBasis());
          }
        }}
      />

      <FormHelperText id="component-error-text" style={{ color: "red" }}>
        {formikProfile.touched.levelExco && formikProfile.errors.levelExco}
      </FormHelperText>
      <Gap height={20} />
      {/* select provinsi */}
      {(levelExco === 2 || levelExco === 3 || levelExco === 4 || levelExco === 5) && (
        <>
          <MyAutocomplete
            id="provinsi"
            name="provinsi"
            fullWidth
            options={optionsProv || []}
            // handleChange={(e, value) => formik.setFieldValue("provinsi", value)}
            size="medium"
            label="Provinsi"
            renderInput={(params) => <MyInput {...params} label="Provinsi" />}
            value={formikProfile.values.provinsi}
            onChange={(e, value) => {
              dispatch(getKabKot(value.id));
              setIdProvinsi(value.id);
              formikProfile.setFieldValue("provinsi", value);
              formikProfile.setFieldValue("kabkot", null);
              formikProfile.setFieldValue("kecamatan", null);
              formikProfile.setFieldValue("kelurahan", null);
            }}
          />
          <FormHelperText id="component-error-text" style={{ color: "red" }}>
            {formikProfile.touched.provinsi && formikProfile.errors.provinsi}
          </FormHelperText>
          <Gap height={10} />
        </>
      )}

      {/* select kabupaten / kota */}

      {(levelExco === 3 || levelExco === 4 || levelExco === 5) && (
        <>
          <MyAutocomplete
            fullWidth
            size="medium"
            label="Kabupaten / Kota*"
            options={optionsKabKot || []}
            id="kabkot"
            name="kabkot"
            value={formikProfile.values.kabkot}
            // handleChange={(e, value) => formikProfile.setFieldValue("kabkot", value)}
            onChange={(e, value) => {
              dispatch(
                getKecamatan(`provinsi=${idProvinsi}&kotaKab=${value.id}`),
              );
              formikProfile.setFieldValue("kabkot", value);
              formikProfile.setFieldValue("kecamatan", null);
              formikProfile.setFieldValue("kelurahan", null);
            }}
          />
          <FormHelperText id="component-error-text" style={{ color: "red" }}>
            {formikProfile.touched.kabkot && formikProfile.errors.kabkot}
          </FormHelperText>
          <Gap height={10} />
        </>
      )}

      {/* select kecamatan */}

      {(levelExco === 4 || levelExco === 5) && (
        <>
          <MyAutocomplete
            fullWidth
            size="medium"
            label="Kecamatan"
            id="kecamatan"
            options={optionsKecamatan || []}
            name="kecamatan"
            value={formikProfile.values.kecamatan}
            onChange={(e, value) => {
              dispatch(getKelurahan(`kecamatan=${value.id}`));
              formikProfile.setFieldValue("kecamatan", value);
              formikProfile.setFieldValue("kelurahan", null);
            }}
          />
          <FormHelperText id="component-error-text" style={{ color: "red" }}>
            {formikProfile.touched.kecamatan && formikProfile.errors.kecamatan}
          </FormHelperText>
          <Gap height={10} />
        </>
      )}

      {/* select kelurahan */}
      {levelExco === 5 && (
        <>
          <MyAutocomplete
            fullWidth
            size="medium"
            label="Kelurahan / Desa*"
            id="kelurahan"
            options={optionsKelurahan || []}
            name="kelurahan"
            value={formikProfile.values.kelurahan}
            onChange={(e, value) => {
              formikProfile.setFieldValue("kelurahan", value);
            }}
          />
          <FormHelperText id="component-error-text" style={{ color: "red" }}>
            {formikProfile.touched.kelurahan && formikProfile.errors.kelurahan}
          </FormHelperText>
          <Gap height={10} />
        </>
      )}

      {/* select komisaris basis */}
      {levelExco === 7 && (
        <>
          <MyAutocomplete
            fullWidth
            size="medium"
            label="Komisaris Basis*"
            id="komisarisBasis"
            options={optionsKomisarisBasis || []}
            name="komisarisBasis"
            value={formikProfile.values.komisarisBasis}
            onChange={(e, value) => {
              formikProfile.setFieldValue("komisarisBasis", value);
            }}
          />
          <FormHelperText id="component-error-text" style={{ color: "red" }}>
            {formikProfile.touched.komisarisBasis && formikProfile.errors.komisarisBasis}
          </FormHelperText>
          <Gap height={10} />
        </>
      )}
      <hr />
      <MyInput
        id="password"
        name="password"
        label="Kata Sandi Baru*"
        fullWidth
        size="medium"
        value={formikProfile.values.password}
        onChange={formikProfile.handleChange}
        error={
          formikProfile.touched.password &&
          Boolean(formikProfile.errors.password)
        }
        helperText={
          formikProfile.touched.password && formikProfile.errors.password
        }
        type={showPasswordNew ? "text" : "password"}
        InputProps={{
          // <-- This is where the toggle button is added.
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => handleClickShowPassword("newPass")}
                // onMouseDown={handleMouseDownPassword}
              >
                {showPasswordNew ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Gap height={20} />
      <MyInput
        id="confirmPassword"
        name="confirmPassword"
        label="Kata Sandi Baru Konfirmasi*"
        fullWidth
        size="medium"
        value={formikProfile.values.confirmPassword}
        onChange={formikProfile.handleChange}
        error={
          formikProfile.touched.confirmPassword &&
          Boolean(formikProfile.errors.confirmPassword)
        }
        helperText={
          formikProfile.touched.confirmPassword &&
          formikProfile.errors.confirmPassword
        }
        type={showPasswordNewConfirm ? "text" : "password"}
        InputProps={{
          // <-- This is where the toggle button is added.
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => handleClickShowPassword("passNewConfirm")}
                // onMouseDown={handleMouseDownPassword}
              >
                {showPasswordNewConfirm ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Gap height={20} />
      <div style={{ display: "flex" }}>
        <MyButton
          height="40px"
          color="#B9B9B9"
          fullWidth
          backgroundColor="#FFFFFFFF"
          borderColor="#B9B9B9"
          backgroundColorHover="#FFFFFFFF"
          variant="outlined"
          children="Batal"
          onClick={handleCloseDialogUser}
        />
        <Gap width={30} />
        <MyButton
          height="40px"
          color="#8B8B8B"
          colorHover="white"
          fullWidth
          backgroundColor="#E8E8E8"
          borderColor="#E8E8E8"
          backgroundColorHover="#F06524"
          variant="outlined"
          children="Simpan"
          type="submit"
          onClick={formikProfile.handleSubmit}
        />
      </div>
    </>
  );
  const classes = useStyles();

  const dataUsers = useSelector((state) => state.users.dataUsers);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [sortBy, setSortBy] = React.useState("name");
  const [sortType, setSortType] = React.useState("asc");
  const [filterLevel, setFilterLevel] = React.useState("");

  useEffect(() => {
    dispatch(getUsers(1));
    dispatch(getProvince());
  }, [dispatch]);

  const handlePagination = (event, value) => {
    setCurrentPage(value);
    dispatch(getUsers(value, search, sortBy, sortType, filterLevel));
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    let temp = event.target.value;
    var timeout;
    var delay = 500;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(function () {
      if (temp === event.target.value) {
        dispatch(
          getUsers(1, event.target.value, sortBy, sortType, filterLevel),
        );
        setCurrentPage(1);
      }
    }, delay);
  };

  const handleFilter = (e) => {
    setFilterLevel(e.target.value);
    setCurrentPage(1);
    dispatch(getUsers(1, search, sortBy, sortType, e.target.value));
  };

  const handleSortBy = (e) => {
    setSortBy(e.target.value);
    dispatch(
      getUsers(currentPage, search, e.target.value, sortType, filterLevel),
    );
  };

  const handleSortType = (e) => {
    setSortType(e.target.value);
    dispatch(
      getUsers(currentPage, search, sortBy, e.target.value, filterLevel),
    );
  };
  return (
    <div style={{ marginLeft: 20, marginRight: 20 }}>
      {/* add new user */}
      <MyDialog
        openDialog={openDialogUser}
        handleCloseDialog={handleCloseDialogUser}
        renderValue={renderFormNewData}
        fullWidth={true}
        maxWidth={"sm"}
      />

      {/* alertMessage */}
      <AlertMessage
        openDialog={openDialogAlert}
        message={message}
        status={status}
        handleCloseDialog={() => setOpenDialogAlert(false)}
      />
      <Gap height={20} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <p style={{ fontSize: 28, fontWeight: 700 }}>Pengaturan Pengguna</p>
        <div style={{ display: "flex", marginTop: 25 }}>
          {/* <Filters /> */}
          <Gap width={10} />

          <MyInput
            size="small"
            id="search"
            name="search"
            // label="Cari"
            placeholder="Cari"
            onChange={handleSearch}
            style={{
              // width: "70%",
              borderRadius: 5,
              backgroundColor: "white",
              height: 40,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Gap width={10} />
          <MyButton
            // style={{ marginTop: 25 }}
            startIcon={<AddIcon />}
            height="63%"
            color="white"
            backgroundColor="#F06524"
            borderColor="#F06524"
            backgroundColorHover="#F06524"
            variant="outlined"
            children="Tambah Pengguna"
            onClick={handleOpenDialogUser}
          />
        </div>
      </div>

      <div className="filter-tabel-exco">
        <div className="filter-tabel-exco-content">
          <p>Saring Tingkat EXCO</p>
          <select onChange={handleFilter}>
            <option value={""}>{"Semua Tingkat Exco"}</option>
            <option value={1}>{"EXCO Pusat"}</option>
            <option value={2}>{"EXCO Provinsi"}</option>
            <option value={3}>{"EXCO Kabupaten / Kota"}</option>
            <option value={4}>{"EXCO Kecamatan"}</option>
            <option value={5}>{"EXCO Kelurahan / Desa"}</option>
            {/* <option value={6}>{"EXCO Komisaris RT / RW"}</option> */}
            <option value={7}>{"EXCO Komisaris Basis"}</option>
          </select>
        </div>

        {/* sort */}
        <div className="filter-tabel-exco-content">
          <p>Urutkan Berdasarkan</p>
          <select onChange={handleSortBy}>
            <option value={"name"}>{"Nama"}</option>
            <option value={"email"}>{"E-mail"}</option>
          </select>
        </div>
        <div className="filter-tabel-exco-content">
          <p>Urutkan Secara</p>
          <select onChange={handleSortType}>
            <option value={"asc"}>{"A-Z (Ascending)"}</option>
            <option value={"desc"}>{"Z-A (Descending)"}</option>
          </select>
        </div>
      </div>

      {/* Tableee */}
      <Table
        currentPage={currentPage}
        search={search}
        sortBy={sortBy}
        sortType={sortType}
        filterLevel={filterLevel}
      />
      <div
        style={{
          justifyContent: "flex-end",
          display: "flex",
          marginTop: "10px",
        }}
      >
        <Pagination
          count={dataUsers?.data?.total_pages}
          variant="outlined"
          shape="rounded"
          page={currentPage}
          onChange={handlePagination}
          className={classes.root}
        />
      </div>
      <Gap height={20} />
      <div className="footer-content"></div>
    </div>
  );
};

export default UserManagement;
